import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { gossipCategories } from "@/mocks/gossipCategories";
import { gossipArticles } from "@/mocks/gossipArticles";
import { gossipTopics } from "@/mocks/gossipTopics";
import { NAV_LINKS } from "@/utils/site";

const tickerItems = [...gossipTopics.slice(0, 6), ...gossipTopics.slice(0, 6)];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const catRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(event.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const keyword = query.trim();
  const results = keyword
    ? gossipArticles
        .filter(
          (item) =>
            item.title.includes(keyword) ||
            item.summary.includes(keyword) ||
            item.tags.some((tag) => tag.includes(keyword))
        )
        .slice(0, 6)
    : [];

  const handleSelect = (slug: string) => {
    setSearchOpen(false);
    setQuery("");
    setMenuOpen(false);
    navigate(`/article/${slug}`);
  };

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap text-sm font-label transition-colors ${
      isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-primary-600 text-background-50">
        <div className="mx-auto flex max-w-shell items-center gap-3 overflow-hidden px-4 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 py-1.5 text-xs font-label font-bold tracking-wide">
            <span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-background-50" />
            实时快讯
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="marquee-track flex w-max items-center gap-10 py-1.5">
              {tickerItems.map((topic, index) => (
                <span key={`${topic.rank}-${index}`} className="whitespace-nowrap text-xs text-background-50/90">
                  {topic.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-shell items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-bold text-background-50">
              瓜
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-bold text-foreground-950">瓜料动态</span>
              <span className="block text-[10px] font-label uppercase tracking-[0.22em] text-foreground-500">
                Gua Feed
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-6 md:flex" aria-label="主导航">
            <NavLink to="/" className={navItemClass} end>
              首页
            </NavLink>
            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((open) => !open)}
                className="flex items-center gap-1 whitespace-nowrap text-sm font-label text-foreground-700 transition-colors hover:text-primary-600"
                aria-expanded={catOpen}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-3 w-64 rounded-lg border border-background-200 bg-background-50 p-2">
                  {gossipCategories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${category.slug}`}
                      onClick={() => setCatOpen(false)}
                      className="flex items-start gap-3 rounded-md px-3 py-2 transition-colors hover:bg-background-100"
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className="ri-price-tag-3-line text-sm" />
                      </span>
                      <span>
                        <span className="block text-sm font-label font-medium text-foreground-900">
                          {category.name}
                        </span>
                        <span className="block text-xs text-foreground-500">{category.tagline}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {NAV_LINKS.filter((link) => link.to !== "/").map((link) =>
              link.to.includes("#") ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className="whitespace-nowrap text-sm font-label text-foreground-700 transition-colors hover:text-primary-600"
                >
                  {link.label}
                </Link>
              ) : (
                <NavLink key={link.to} to={link.to} className={navItemClass}>
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((open) => !open)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              aria-label="搜索瓜料"
            >
              <i className={searchOpen ? "ri-close-line text-base" : "ri-search-line text-base"} />
            </button>
            <span className="hidden items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-label text-accent-800 md:flex">
              <span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-accent-500" />
              更新中
            </span>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden"
              aria-label="打开菜单"
            >
              <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-background-200 bg-background-50">
            <div className="mx-auto max-w-shell px-4 py-4 md:px-6">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5">
                <i className="ri-search-line text-base text-foreground-500" />
                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="搜索瓜料、话题或关键词…"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              {keyword && (
                <div className="mt-3">
                  {results.length > 0 ? (
                    <ul className="divide-y divide-background-200 overflow-hidden rounded-md border border-background-200">
                      {results.map((item) => (
                        <li key={item.slug}>
                          <button
                            type="button"
                            onClick={() => handleSelect(item.slug)}
                            className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-background-100"
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-sm text-foreground-900">{item.title}</span>
                              <span className="mt-0.5 block text-xs text-foreground-500">{item.categoryName} · {item.relativeTime}</span>
                            </span>
                            <i className="ri-arrow-right-line shrink-0 text-base text-foreground-400" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="rounded-md border border-background-200 px-4 py-4 text-sm text-foreground-500">
                      暂时没有匹配的瓜料，换个关键词试试。
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 md:hidden">
            <nav className="mx-auto flex max-w-shell flex-col px-4 py-3" aria-label="移动端导航">
              <NavLink
                to="/"
                end
                onClick={() => setMenuOpen(false)}
                className="border-b border-background-200 py-3 text-sm font-label text-foreground-800"
              >
                首页
              </NavLink>
              {gossipCategories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="border-b border-background-200 py-3 text-sm font-label text-foreground-800"
                >
                  {category.name}
                </Link>
              ))}
              {NAV_LINKS.filter((link) => link.to !== "/").map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="border-b border-background-200 py-3 text-sm font-label text-foreground-800 last:border-0"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}